import { isDefined } from "@clipboard-health/util-ts";
import { isBefore, startOfDay } from "date-fns";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { ModalRoute } from "../Navigation/ModalRoute";
import { ModalTransitionSwitch } from "../Navigation/ModalTransitionSwitch";
import { useInHomeVisitModalsDataContext } from "../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { ConfirmVisitFinalizeBottomSheet } from "./BookedVisitDetails/ConfirmVisitFinalizeBottomSheet";
import { ConfirmVisitOptionsBottomSheet } from "./BookedVisitDetails/ConfirmVisitOptionsBottomSheet";
import { DropVisitBottomSheet } from "./BookedVisitDetails/DropVisitBottomSheet";
import { LogVisitBottomSheet } from "./BookedVisitDetails/LogVisitDateBottomSheet";
import { LogVisitEndTimeBottomSheet } from "./BookedVisitDetails/LogVisitEndTimeBottomSheet";
import { LogVisitFinalizeBottomSheet } from "./BookedVisitDetails/LogVisitFinalizeBottomSheet";
import { LogVisitStartTimeBottomSheet } from "./BookedVisitDetails/LogVisitStartTimeBottomSheet";
import { PendVisitBottomSheet } from "./BookedVisitDetails/PendVisitBottomSheet";
import { VisitConfirmedBottomSheet } from "./BookedVisitDetails/VisitConfirmedBottomSheet";
import { VisitDroppedBottomSheet } from "./BookedVisitDetails/VisitDroppedBottomSheet";
import { VisitLoggedBottomSheet } from "./BookedVisitDetails/VisitLoggedBottomSheet";
import {
  BOOKED_VISIT_CONFIRM_DATE_CONFIRMED_MODAL_PATH,
  BOOKED_VISIT_CONFIRM_DATE_FINALIZE_MODAL_PATH,
  BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH,
  BOOKED_VISIT_DROP_CONFIRMED_MODAL_PATH,
  BOOKED_VISIT_DROP_MODAL_PATH,
  BOOKED_VISIT_LOG_CONFIRMED_MODAL_PATH,
  BOOKED_VISIT_LOG_DATE_MODAL_PATH,
  BOOKED_VISIT_LOG_END_TIME_MODAL_PATH,
  BOOKED_VISIT_LOG_FINALIZE_MODAL_PATH,
  BOOKED_VISIT_LOG_START_TIME_MODAL_PATH,
  BOOKED_VISIT_PEND_MODAL_PATH,
} from "./paths";

export function BookedVisitModalRoutes() {
  const { visit, baseNavigationPath, generateModalPath } = useInHomeVisitModalsDataContext();
  const startOfToday = startOfDay(new Date());
  const history = useHistory();
  const { attributes: visitAttributes } = visit ?? {};
  const { pricingType } = visitAttributes ?? {};

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [loggedVisitDate, setLoggedVisitDate] = useState<Date | undefined>(undefined);
  const [loggedVisitStartTime, setLoggedVisitStartTime] = useState<Date | undefined>(undefined);
  const [loggedVisitEndTime, setLoggedVisitEndTime] = useState<Date | undefined>(undefined);

  return (
    <ModalTransitionSwitch>
      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_LOG_CONFIRMED_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit)) {
            return null;
          }

          return <VisitLoggedBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_LOG_FINALIZE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (
            !isDefined(visit) ||
            !isDefined(loggedVisitDate) ||
            !isDefined(loggedVisitStartTime) ||
            (pricingType === "PER_HOUR" && !isDefined(loggedVisitEndTime))
          ) {
            return null;
          }

          return (
            <LogVisitFinalizeBottomSheet
              modalState={modalState}
              visitDate={loggedVisitDate}
              visitStartTime={loggedVisitStartTime}
              visitEndTime={loggedVisitEndTime}
            />
          );
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_LOG_START_TIME_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit) || !isDefined(loggedVisitDate)) {
            return null;
          }

          return (
            <LogVisitStartTimeBottomSheet
              modalState={modalState}
              visitDate={loggedVisitDate}
              onConfirm={(newSelectedStartTime: Date) => {
                setLoggedVisitStartTime(newSelectedStartTime);
                const nextModalPath =
                  pricingType === "PER_HOUR"
                    ? BOOKED_VISIT_LOG_END_TIME_MODAL_PATH
                    : BOOKED_VISIT_LOG_FINALIZE_MODAL_PATH;
                history.replace(
                  generateModalPath(nextModalPath, {
                    caseId: visit.attributes.caseId,
                    visitId: visit.id,
                  })
                );
              }}
            />
          );
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_LOG_END_TIME_MODAL_PATH}`}
        render={({ modalState }) => {
          if (
            !isDefined(visit) ||
            !isDefined(loggedVisitDate) ||
            !isDefined(loggedVisitStartTime)
          ) {
            return null;
          }

          return (
            <LogVisitEndTimeBottomSheet
              modalState={modalState}
              visitDate={loggedVisitDate}
              startAt={loggedVisitStartTime}
              onConfirm={(newSelectedEndTime: Date) => {
                setLoggedVisitEndTime(newSelectedEndTime);
                history.replace(
                  generateModalPath(BOOKED_VISIT_LOG_FINALIZE_MODAL_PATH, {
                    caseId: visit.attributes.caseId,
                    visitId: visit.id,
                  })
                );
              }}
            />
          );
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_LOG_DATE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit)) {
            return null;
          }

          return (
            <LogVisitBottomSheet
              modalState={modalState}
              visitDate={loggedVisitDate}
              onConfirm={(newSelectedDate: Date) => {
                setLoggedVisitDate(newSelectedDate);
                history.replace(
                  generateModalPath(BOOKED_VISIT_LOG_START_TIME_MODAL_PATH, {
                    caseId: visit.attributes.caseId,
                    visitId: visit.id,
                  })
                );
              }}
            />
          );
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_DROP_CONFIRMED_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit)) {
            return null;
          }

          return <VisitDroppedBottomSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_DROP_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit)) {
            return null;
          }

          return <DropVisitBottomSheet modalState={modalState} />;
        }}
      />
      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_PEND_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit)) {
            return null;
          }

          return <PendVisitBottomSheet modalState={modalState} />;
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_CONFIRM_DATE_FINALIZE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit) || !isDefined(selectedDate)) {
            return null;
          }

          return (
            <ConfirmVisitFinalizeBottomSheet modalState={modalState} visitDate={selectedDate} />
          );
        }}
      />

      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_CONFIRM_DATE_CONFIRMED_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit) || !isDefined(selectedDate)) {
            return null;
          }

          return <VisitConfirmedBottomSheet modalState={modalState} visitDate={selectedDate} />;
        }}
      />
      <ModalRoute
        path={`${baseNavigationPath}/${BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH}`}
        render={({ modalState }) => {
          if (!isDefined(visit)) {
            return null;
          }

          return (
            <ConfirmVisitOptionsBottomSheet
              visitDate={selectedDate}
              modalState={modalState}
              onConfirm={(date) => {
                const isPreviousVisit = isDefined(date) && isBefore(date, startOfToday);
                if (isPreviousVisit) {
                  setLoggedVisitDate(date);
                  history.replace(
                    generateModalPath(BOOKED_VISIT_LOG_DATE_MODAL_PATH, {
                      caseId: visit.attributes.caseId,
                      visitId: visit.id,
                    })
                  );
                } else {
                  setSelectedDate(date);
                  history.replace(
                    generateModalPath(BOOKED_VISIT_CONFIRM_DATE_FINALIZE_MODAL_PATH, {
                      caseId: visit.attributes.caseId,
                      visitId: visit.id,
                    })
                  );
                }
              }}
            />
          );
        }}
      />
    </ModalTransitionSwitch>
  );
}
